import { API_BASE } from "@/lib/api-config";
import { authFetch } from "@/lib/authenticated-fetch";
import { getAccessToken } from "@/lib/local-auth";
import type { TimesheetProjectRow, TimesheetReportMeta } from "@/lib/timesheet-shared";

function messageFromApiErrorBody(body: { error?: unknown }): string {
  const e = body.error;
  if (typeof e === "string") return e;
  return "Permintaan gagal";
}

export type TimesheetEmployeeImport = {
  name: string;
  hourlyRate: number | null;
  totals: {
    loggedTime: string;
    activeTime: string;
    activityPct: number;
    cost: number;
    sessionCount?: number;
  };
  projects: TimesheetProjectRow[];
};

export type RateIncentiveItem = {
  id: string;
  jenis: "jabatan" | "wfo";
  nominal: number;
  catatan?: string;
};

/** Bentuk JSONB kolom `rate.incentive` */
export type RateIncentivePayload = {
  name_employee: string;
  total_incentive: number;
  total_count: number;
  items: RateIncentiveItem[];
};

export type RateTaxItem = {
  id: string;
  jenis: "pph21" | "pph23" | "lainnya";
  nominal: number;
  catatan?: string;
};

/** Bentuk JSONB kolom `rate.tax` */
export type RateTaxPayload = {
  name_employee: string;
  total_tax: number;
  total_count: number;
  items: RateTaxItem[];
};

export type RateRow = {
  id: string;
  period_id: number;
  name_employee: string;
  rate_hours: number | null;
  total_hours: string;
  total_activity: number | null;
  total_earn: number | null;
  details_project: unknown;
  additional_hours?: unknown | null;
  /** JSONB dari sinkron save-reimburse: name_employee, total_reimburse, total_count, details_reimburse[] */
  reimburse?: unknown | null;
  /** JSONB: name_employee, total_incentive, total_count, items[] */
  incentive?: unknown | null;
  /** JSONB: name_employee, total_tax, total_count, items[] */
  tax?: unknown | null;
  /** JSONB: name_employee, total_days, total_hours, hours_per_day, reason, proof, dates[] */
  paid_leave?: unknown | null;
  created_at?: string;
};

export type RatePaidLeavePayload = {
  name_employee: string;
  total_days: number;
  total_hours: number;
  hours_per_day: number;
  reason: string;
  proof: "terlampir" | "tidak terlampir";
  dates: string[];
  /** Subset dari `dates`: hari cuti dengan kewajiban kerja (disimpan ke JSONB). */
  work_required_dates?: string[];
};

export async function importTimesheetToRate(
  periodId: string,
  employees: TimesheetEmployeeImport[],
  report?: TimesheetReportMeta,
): Promise<unknown> {
  const token = getAccessToken();
  if (!token) throw new Error("Belum login atau token tidak tersedia.");

  const res = await authFetch(`${API_BASE}/api/v1/rate/import-timesheet`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      period_id: Number(periodId),
      employees,
      report,
    }),
  });

  const data = (await res.json().catch(() => ({}))) as { data?: unknown; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  return data.data ?? null;
}

export async function fetchRatesByPeriod(periodId: string): Promise<RateRow[]> {
  const token = getAccessToken();
  if (!token) throw new Error("Belum login atau token tidak tersedia.");

  const url = new URL(`${API_BASE}/api/v1/rate`);
  url.searchParams.set("period_id", String(Number(periodId)));

  const res = await authFetch(url.toString(), {
    method: "GET",
  });

  const data = (await res.json().catch(() => ({}))) as { data?: RateRow[]; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  return data.data ?? [];
}

export async function saveRateIncentive(
  periodId: string,
  nameEmployee: string,
  items: RateIncentiveItem[],
): Promise<RateIncentivePayload> {
  const token = getAccessToken();
  if (!token) throw new Error("Belum login atau token tidak tersedia.");

  const res = await authFetch(`${API_BASE}/api/v1/rate/incentive`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      period_id: Number(periodId),
      name_employee: nameEmployee.trim(),
      items,
    }),
  });

  const data = (await res.json().catch(() => ({}))) as { data?: { incentive?: RateIncentivePayload }; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  const inc = data.data?.incentive;
  if (!inc) throw new Error("Respons tidak berisi incentive");
  return inc;
}

export async function saveRateTax(
  periodId: string,
  nameEmployee: string,
  items: RateTaxItem[],
): Promise<RateTaxPayload> {
  const token = getAccessToken();
  if (!token) throw new Error("Belum login atau token tidak tersedia.");

  const res = await authFetch(`${API_BASE}/api/v1/rate/tax`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      period_id: Number(periodId),
      name_employee: nameEmployee.trim(),
      items,
    }),
  });

  const data = (await res.json().catch(() => ({}))) as { data?: { tax?: RateTaxPayload }; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  const tax = data.data?.tax;
  if (!tax) throw new Error("Respons tidak berisi tax");
  return tax;
}

export async function saveRatePaidLeave(
  periodId: string,
  nameEmployee: string,
  args: {
    dates: string[];
    reason: string;
    proof: "terlampir" | "tidak terlampir";
    hoursPerDay?: number;
    /** Tanggal (YYYY-MM-DD) di mana karyawan wajib kerja meskipun cuti. */
    workRequiredDates?: string[];
  },
): Promise<RatePaidLeavePayload | null> {
  const token = getAccessToken();
  if (!token) throw new Error("Belum login atau token tidak tersedia.");

  const res = await authFetch(`${API_BASE}/api/v1/rate/paid-leave`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      period_id: Number(periodId),
      name_employee: nameEmployee.trim(),
      dates: args.dates,
      reason: args.reason,
      proof: args.proof,
      hours_per_day: args.hoursPerDay ?? 8,
      work_required_dates: args.workRequiredDates ?? [],
    }),
  });

  const data = (await res.json().catch(() => ({}))) as { data?: { paid_leave?: RatePaidLeavePayload | null }; error?: unknown };
  if (!res.ok) throw new Error(messageFromApiErrorBody(data));
  return data.data?.paid_leave ?? null;
}

